import React from 'react';
import { Badge } from '../ui/badge';
import { Clock, Edit3, Plus, ShieldCheck } from 'lucide-react';
import { formatDateTimeIST } from '../../utils/date';
import { MilestoneEmpty } from './ClinicalMilestoneTimeline';

const SECTION_LABEL = {
  chief_complaints: 'Chief Complaints',
  history_of_present_illness: 'History of Present Illness',
  past_history: 'Past History',
  tumor_board: 'Tumor Board',
  examination: 'Clinical Examination',
  general_info: 'General Info / Vitals',
  diagnosis: 'Diagnosis',
  prescription: 'Prescription',
  investigations: 'Investigations',
  advice: 'Advice',
  review_date: 'Review Date',
  admission_request: 'Admission Request',
};

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function renderValue(val) {
  if (val === null || val === undefined || val === '' || (Array.isArray(val) && !val.length)) {
    return <span className="italic text-gray-400">(empty)</span>;
  }
  if (Array.isArray(val)) {
    return (
      <ul className="ml-4 list-disc space-y-0.5">
        {val.map((item, i) => (
          <li key={i} className="text-xs">
            {typeof item === 'string' ? stripHtml(item) : (item?.medicine_name || item?.name || JSON.stringify(item))}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof val === 'object') {
    return (
      <pre className="max-h-28 overflow-auto rounded bg-white/60 p-1.5 font-mono text-[11px]">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  const text = typeof val === 'string' ? stripHtml(val) : String(val);
  return <span className="break-words whitespace-pre-wrap">{text.slice(0, 240)}</span>;
}

/** Active-consultation audit entries for Visit History drawer. */
export default function VisitHistoryAuditPanel({ entries }) {
  if (!entries?.length) {
    return (
      <MilestoneEmpty
        icon={ShieldCheck}
        text="No audit entries yet"
        subtext="Edits will appear here with before/after values."
      />
    );
  }

  return (
    <div className="space-y-2 pb-3" data-testid="audit-list">
      {entries.map((entry, i) => {
        const isEdit = entry.action === 'edit';
        const isPost = !!entry.post_completion;
        return (
          <div
            key={`${entry.section}-${entry.timestamp}-${i}`}
            className={`rounded-lg border p-3 ${isPost ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}
            data-testid={`audit-entry-${entry.section}-${entry.timestamp}`}
          >
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {isEdit ? <Edit3 className="h-4 w-4 text-amber-600" /> : <Plus className="h-4 w-4 text-emerald-600" />}
                <span className="text-sm font-semibold text-gray-800">
                  {SECTION_LABEL[entry.section] || entry.section}
                </span>
                <Badge variant="outline" className="text-[10px]">{isEdit ? 'Edited' : 'Added'}</Badge>
                {isPost ? <Badge className="bg-amber-600 text-[10px] text-white">Post-completion</Badge> : null}
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gray-500">
                <Clock className="h-3 w-3" />
                {entry.timestamp ? formatDateTimeIST(entry.timestamp) : '—'}
              </div>
            </div>
            <div className="mb-2 text-[11px] text-gray-500">
              By <span className="font-medium text-gray-700">{entry.user_name || 'Doctor'}</span>
            </div>
            {isEdit ? (
              <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                <div className="rounded border border-red-200 bg-red-50/50 p-2">
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-red-600">Before</div>
                  {renderValue(entry.old_value)}
                </div>
                <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2">
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">After</div>
                  {renderValue(entry.new_value)}
                </div>
              </div>
            ) : (
              <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2 text-xs text-gray-700">
                {renderValue(entry.new_value)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
